import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { UnreadVoicemailStatusFilter } from '~/types/ApiRequestTypes'

import {
  ApiError,
  Extension,
  PaginatedApiResult,
  Voicemail,
  VoicemailInstance,
} from '@mangovoice/types'

export const VoicemailQueryKey = {
  VOICEMAILS: 'voicemails',
  VOICEMAIL: 'voicemail',
  EXTENSIONS: 'extensions',
  ALL_EXTENSIONS: 'all_extensions',
}

interface UseGetVoicemailsParams {
  pbxId: number
  extId?: string | number
  search?: string
  unread: UnreadVoicemailStatusFilter
  onRequestCompleted?: (data: PaginatedApiResult<Voicemail>) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<Voicemail>, ApiError>,
    'queryKey'
  >
}

export const useGetVoicemails = ({
  pbxId,
  extId,
  search,
  unread,
  onRequestCompleted,
  options,
}: UseGetVoicemailsParams) => {
  const api = useMangoApi()
  const [params, setParams] = useState<{ offset: number }>()

  const query = useQuery<PaginatedApiResult<Voicemail>, ApiError>({
    queryKey: [
      VoicemailQueryKey.VOICEMAILS,
      { extId, search, unread, offset: params?.offset ?? 0 },
    ],
    queryFn: async () => {
      if (!pbxId) {
        return Promise.reject({} as ApiError)
      }

      const { ok, data } = await api.getVoicemails({
        pbxId,
        extensionId: extId,
        search,
        offset: params?.offset,
        unread,
      })

      if (!ok) {
        return Promise.reject(data)
      }

      onRequestCompleted?.(data!)
      return data!
    },
    ...options,
    retry: options?.retry ?? false,
  })

  const refetch = (offset: number) => {
    setParams({ offset })
  }

  useEffect(() => {
    if (params?.offset === undefined) {
      return
    }

    query.refetch()
  }, [params])

  return {
    ...query,
    refetch,
  }
}

interface UseGetVoicemailParams {
  voicemailId: number
  onRequestCompleted?: (data: VoicemailInstance) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<VoicemailInstance, ApiError | null>,
    'queryKey'
  >
}

export const useGetVoicemail = ({
  voicemailId,
  onRequestCompleted,
  onRequestErrored,
  options,
}: UseGetVoicemailParams) => {
  const api = useMangoApi()
  return useQuery<VoicemailInstance, ApiError | null>({
    queryKey: [VoicemailQueryKey.VOICEMAIL, { voicemailId }],
    queryFn: async () => {
      if (!voicemailId) {
        return Promise.reject(null)
      }
      const { ok, data, status } = await api.getVoicemail(voicemailId)
      if (!ok) {
        const error = { status, ...data }
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      onRequestCompleted?.(data!)
      return data!
    },
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseDeleteVoicemailParams {
  onRequestCompleted?: ({
    data,
    voicemailId,
  }: {
    data: { message: string }
    voicemailId: number
  }) => void
}

export const useDeleteVoicemail = ({
  onRequestCompleted,
}: UseDeleteVoicemailParams) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: (voicemailId: number) => api.deleteVoicemail(voicemailId),
    onSuccess: (result, variables) => {
      onRequestCompleted?.({
        data: result.data as { message: string },
        voicemailId: variables,
      })
    },
  })
}

interface UseMarkVoicemailReadParams {
  onRequestCompleted?: ({
    data,
    voicemailId,
  }: {
    data: { message: string }
    voicemailId: number
  }) => void
}
export const useMarkVoicemailRead = ({
  onRequestCompleted,
}: UseMarkVoicemailReadParams) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: (voicemailId: number) => api.markVoicemailRead(voicemailId),
    onSuccess: (response, variables) => {
      onRequestCompleted?.({
        data: response.data as { message: string },
        voicemailId: variables,
      })
    },
    retry: false,
  })
}

interface UseMarkVoicemailUnreadParams {
  onRequestCompleted?: ({
    data,
    voicemailId,
  }: {
    data: { message: string }
    voicemailId: number
  }) => void
}
export const useMarkVoicemailUnread = ({
  onRequestCompleted,
}: UseMarkVoicemailUnreadParams) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: (voicemailId: number) => api.markVoicemailUnread(voicemailId),
    onSuccess: (response, variables) => {
      onRequestCompleted?.({
        data: response.data as { message: string },
        voicemailId: variables,
      })
    },
    retry: false,
  })
}

export const useMoveVoicemail = () => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: ({
      voicemailId,
      extensionId,
    }: {
      voicemailId: number
      extensionId: number
    }) => api.moveVoicemail(voicemailId, extensionId),
    onError: (error) => {
      console.error('Error moving voicemail:', error)
    },
    retry: false,
  })
}

interface UseGetVoicemailExtensionsParams {
  pbxId: number
  onRequestCompleted?: (data: PaginatedApiResult<Extension>) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<Extension>, ApiError>,
    'queryKey'
  >
}

export const useGetVoicemailExtensions = ({
  pbxId,
  onRequestCompleted,
  onRequestErrored,
  options = {},
}: UseGetVoicemailExtensionsParams) => {
  const api = useMangoApi()
  return useQuery<PaginatedApiResult<Extension>, ApiError>({
    queryKey: [VoicemailQueryKey.EXTENSIONS, pbxId],
    queryFn: async () => {
      if (pbxId === 0) {
        return Promise.reject({} as ApiError)
      }

      const { ok, data, status } = await api.getVoicemailExtensions(pbxId)
      if (!ok) {
        const error = { status, ...data }
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      onRequestCompleted?.(data!)
      return data!
    },
    ...options,
    retry: options.retry ?? false,
  })
}

interface UseGetAllExtensionsParams {
  pbxId: number
  onRequestCompleted?: (data: PaginatedApiResult<Extension>) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<Extension>, ApiError>,
    'queryKey'
  >
}

export const useGetAllExtensions = ({
  pbxId,
  onRequestCompleted,
  onRequestErrored,
  options = {},
}: UseGetAllExtensionsParams) => {
  const api = useMangoApi()
  return useQuery<PaginatedApiResult<Extension>, ApiError>({
    queryKey: [VoicemailQueryKey.ALL_EXTENSIONS, pbxId],
    queryFn: async () => {
      if (!pbxId) {
        return Promise.reject({} as ApiError)
      }

      const { ok, data, status } = await api.getAllExtensions(pbxId)
      if (!ok) {
        const error = { status, ...data }
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      onRequestCompleted?.(data!)
      return data!
    },
    ...options,
    retry: options.retry ?? false,
  })
}
